/**
 * Personal login for Manager & Vendedor.
 * Admin creates their credentials from /admin/staff. Landing on success
 * routes to /admin (admin), /manager (manager) or /magazine (seller).
 */
import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogIn, Mail, KeyRound, ShieldCheck, Loader2, Info, ArrowLeft, User, Users } from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useGate } from "@/context/GateContext";
import { CanalesLogo } from "@/components/CanalesLogo";
import { Input } from "@/components/ui/input";

const ROLE_META = {
  admin:   { label_es: "Admin",    label_en: "Admin",     color: "bg-canales-red text-white",    Icon: ShieldCheck },
  manager: { label_es: "Manager",  label_en: "Manager",   color: "bg-canales-blue text-white",   Icon: Users },
  seller:  { label_es: "Vendedor", label_en: "Seller",    color: "bg-canales-yellow text-black", Icon: User },
};

const routeForRole = (r) =>
  r === "manager" ? "/manager/team" : (r === "seller" ? "/magazine" : "/admin");

export default function StaffLogin() {
  const { lang } = useLang();
  const { refresh } = useAuth() || {};
  const gate = useGate() || {};
  const nav = useNavigate();
  const [params] = useSearchParams();

  // Role hint from the gate role-picker. Purely cosmetic — the backend
  // routes purely by the role stored on the account.
  const rolePicked = useMemo(() => {
    const r = (params.get("role") || "").toLowerCase();
    return ["admin", "manager", "seller"].includes(r) ? r : null;
  }, [params]);
  const roleMeta = rolePicked ? ROLE_META[rolePicked] : null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    try {
      const { data } = await api.post("/staff/login", {
        username: email.trim(),
        password,
      });
      localStorage.setItem("has_session", "1");
      if (refresh) await refresh();
      // Re-verify the app-wide gate — the backend just issued a gate_token
      // cookie for us so the gate state should flip from `locked` to
      // `unlocked` on the next check. Without this the route-level gate
      // check bounces us back to the role-picker screen.
      if (gate?.check) await gate.check();
      toast.success(
        lang === "es" ? `Bienvenido, ${data.name}` : `Welcome, ${data.name}`
      );
      if (data.must_change_password) {
        nav("/change-password", { replace: true });
        return;
      }
      // Mismatch guard — if the user picked a role but their account has a
      // different one, we warn softly but still send them to the right page.
      if (rolePicked && data.role !== rolePicked && data.role !== "owner") {
        toast.message(
          lang === "es"
            ? `Tu cuenta es ${data.role}, no ${rolePicked}. Te llevamos a tu panel.`
            : `Your account is ${data.role}, not ${rolePicked}. Redirecting.`
        );
      }
      nav(routeForRole(data.role), { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error de login");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10 bg-halftone"
      data-testid="staff-login-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md brutal p-8 bg-white space-y-6"
      >
        <div className="text-center">
          <CanalesLogo width={160} className="mx-auto mb-2" />
          {roleMeta ? (
            <div
              className={`inline-flex items-center gap-2 border-2 border-black rounded-full px-3 py-1 font-display font-bold text-xs uppercase tracking-widest ${roleMeta.color}`}
              data-testid={`staff-login-role-${rolePicked}`}
            >
              <roleMeta.Icon className="w-4 h-4" />
              {lang === "es" ? `Entrar como ${roleMeta.label_es}` : `Sign in as ${roleMeta.label_en}`}
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 bg-canales-yellow border-2 border-black rounded-full px-3 py-1 font-display font-bold text-xs uppercase tracking-widest">
              <ShieldCheck className="w-4 h-4" />
              {lang === "es" ? "Iniciar sesión" : "Sign in"}
            </div>
          )}
          <h1 className="mt-3 font-display font-black text-3xl">
            {lang === "es" ? "Iniciar sesión" : "Sign in"}
          </h1>
          <p className="mt-1 text-sm font-body text-neutral-600">
            {lang === "es"
              ? "Entra con tu email corporativo de Canales."
              : "Sign in with your Canales corporate email."}
          </p>
        </div>

        <div className="brutal-sm bg-canales-cream p-3 flex items-start gap-2 text-xs font-body text-neutral-800">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-canales-blue" />
          <div>
            {lang === "es" ? (
              <>
                <span className="font-display font-black">¿Primera vez?</span>{" "}
                Usa la contraseña temporal{" "}
                <code className="brutal-sm bg-white px-1.5 py-0.5 font-mono">Canales2026</code>{" "}
                y te pediremos crear la tuya.
              </>
            ) : (
              <>
                <span className="font-display font-black">First time?</span>{" "}
                Use the temporary password{" "}
                <code className="brutal-sm bg-white px-1.5 py-0.5 font-mono">Canales2026</code>{" "}
                and we&apos;ll ask you to create your own.
              </>
            )}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
              {lang === "es" ? "Email corporativo" : "Corporate email"}
            </span>
            <div className="mt-1 relative">
              <Mail className="absolute left-3 top-3.5 w-5 h-5 text-neutral-500" />
              <Input
                data-testid="staff-login-username"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@canalesfurniture.org"
                autoFocus
                autoComplete="email"
                inputMode="email"
                className="brutal-sm pl-10 py-6 text-lg lowercase"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
              {lang === "es" ? "Contraseña" : "Password"}
            </span>
            <div className="mt-1 relative">
              <KeyRound className="absolute left-3 top-3.5 w-5 h-5 text-neutral-500" />
              <Input
                data-testid="staff-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="••••••••"
                className="brutal-sm pl-10 py-6 text-lg"
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={busy}
            data-testid="staff-login-submit"
            className="w-full brutal-btn bg-canales-blue text-white px-6 py-4 text-lg flex items-center justify-center gap-2 font-display font-black disabled:opacity-60"
          >
            {busy ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {lang === "es" ? "Entrando…" : "Signing in…"}
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                {lang === "es" ? "Entrar" : "Sign in"}
              </>
            )}
          </button>
        </form>

        <div className="border-t-2 border-dashed border-neutral-300 pt-4 text-center text-xs font-body text-neutral-600 space-y-2">
          <Link
            to="/"
            className="inline-flex items-center gap-1 font-display font-bold text-canales-red underline"
            data-testid="staff-login-back-picker"
            onClick={async (e) => {
              // Defensive logout: if a stale session cookie is lying around
              // from another user, kill it before jumping to the role picker
              // so the app really re-asks for credentials.
              try {
                await api.post("/auth/logout").catch(() => {});
              } finally {
                localStorage.removeItem("has_session");
              }
            }}
          >
            <ArrowLeft className="w-3 h-3" />
            {lang === "es" ? "Cambiar tipo de usuario" : "Change user type"}
          </Link>
          <div>
            {lang === "es"
              ? "El acceso con Google fue deshabilitado. Todos entran aquí con su email + contraseña."
              : "Google sign-in is disabled. All users sign in here with email + password."}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
